import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { api, ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Intestazione, Vuoto } from '../../componenti/Pezzi.tsx'
import { Etichetta, Riquadro, Tabella } from '../../componenti/base.tsx'
import { IconaCalendario } from '../../componenti/Icone.tsx'
import {
  STATI, doveLeggibile, durataLeggibile, giornoDiOggi, giornoVicino, type MiaMediazione,
} from '../../servizi/mediazioni.ts'

/**
 * «Le mie mediazioni»: il mediatore vede quelle che gli hanno assegnato, con giorno e ora in
 * evidenza, e da qui le apre per completare i dati della persona (indicazioni CIDIS del
 * 17/10/2026). In programma prima, poi quelle concluse nell'ultimo mese.
 */

/** Che cosa resta da fare su una mediazione in programma, detto in breve. */
function DaFare({ m }: { m: MiaMediazione }) {
  if (m.stato !== 'programmata') return null
  const passata = m.data < giornoDiOggi()
  return (
    <span className="riga-etichette">
      {passata && <Etichetta tono="attenzione">esito da segnare</Etichetta>}
      {(m.da_completare ?? 0) > 0
        ? <Etichetta tono="accento">
            {m.da_completare === 1 ? 'un dato da completare' : `${m.da_completare} dati da completare`}
          </Etichetta>
        : <Etichetta tono="positivo">dati completi</Etichetta>}
    </span>
  )
}

/**
 * Una mediazione come voce cliccabile: giorno e ora a sinistra, persona e luogo a destra.
 * `compatta` toglie la cornice del riquadro, per l'elenco dentro il riquadro del cruscotto.
 */
export function VoceMediazione({ m, compatta = false }: { m: MiaMediazione; compatta?: boolean }) {
  const passata = m.data < giornoDiOggi()
  return (
    <Link to={`/mediazioni/${m.id}`} className={compatta ? 'mediazioni-voce' : 'riquadro mediazioni-voce'}>
      <span className={`mediazioni-quando${passata ? ' mediazioni-quando-passato' : ''}`}>
        <span className="mediazioni-quando-giorno">{giornoVicino(m.data)}</span>
        <span className="mediazioni-quando-ora">{m.ora}</span>
      </span>
      <span className="mediazioni-testo">
        <span className="mediazioni-chi">{m.persona.nome} {m.persona.cognome}</span>
        <span className="mediazioni-dettagli">
          {doveLeggibile(m)} · {durataLeggibile(m.durata_minuti)}{m.lingua_nome && ` · ${m.lingua_nome}`}
        </span>
        <DaFare m={m} />
      </span>
    </Link>
  )
}

export function LeMieMediazioni() {
  const [mie, setMie] = useState<MiaMediazione[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  useEffect(() => {
    api.get<MiaMediazione[]>('/mediazioni/mie')
      .then(setMie)
      .catch((e) => setErrore(e instanceof ErroreApi ? e.message : 'Non riesco a caricare le tue mediazioni.'))
  }, [])

  const programmate = mie?.filter((m) => m.stato === 'programmata') ?? []
  const concluse = mie?.filter((m) => m.stato !== 'programmata') ?? []

  return (
    <>
      <Intestazione titolo="Le mie mediazioni"
                    sottotitolo="Le mediazioni che ti hanno assegnato. Aprine una per vedere i dettagli e inserire i dati che mancano."
                    azioni={<Link className="bottone bottone-secondario" to="/mediazioni/mio-calendario">Il mio calendario</Link>} />
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!mie && !errore && <Attesa testo="Caricamento…" />}

      {mie && (
        <>
          <h2 className="rete-titoletto titolo-con-icona"><IconaCalendario /> In programma</h2>
          {programmate.length
            ? (
              <ul className="mediazioni-elenco">
                {programmate.map((m) => <li key={m.id}><VoceMediazione m={m} /></li>)}
              </ul>
            )
            : <Riquadro><Vuoto testo="Per ora non hai mediazioni in programma. Quando te ne assegnano una, ricevi un avviso." /></Riquadro>}

          {concluse.length > 0 && (
            <Riquadro denso titolo="Concluse nell’ultimo mese">
              <Tabella intestazioni={['Quando', 'Persona', 'Dove', 'Esito']}>
                {concluse.map((m) => (
                  <tr key={m.id}>
                    <td data-etichetta="Quando">
                      <Link to={`/mediazioni/${m.id}`}>{giornoVicino(m.data)} alle {m.ora}</Link>
                    </td>
                    <td data-etichetta="Persona">{m.persona.nome} {m.persona.cognome}</td>
                    <td data-etichetta="Dove">{doveLeggibile(m)}</td>
                    <td data-etichetta="Esito">
                      <Etichetta tono={STATI[m.stato].tono}>{STATI[m.stato].etichetta}</Etichetta>
                      {m.stato === 'svolta' && m.ore_svolte != null && (
                        <span className="piccolo tenue"> {durataLeggibile(m.ore_svolte * 60)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </Tabella>
            </Riquadro>
          )}
        </>
      )}
    </>
  )
}

/** Riquadro del cruscotto: le prossime mediazioni del mediatore. */
export function ProssimeMediazioni() {
  const [mie, setMie] = useState<MiaMediazione[] | null>(null)
  useEffect(() => {
    api.get<MiaMediazione[]>('/mediazioni/mie?solo_programmate=true&limite=5')
      .then(setMie).catch(() => setMie([]))
  }, [])
  return (
    <Riquadro denso titolo={<span className="titolo-con-icona"><IconaCalendario /> Le tue prossime mediazioni</span>}
              azioni={<Link to="/mediazioni">Tutte le mie mediazioni</Link>}>
      {!mie
        ? <Attesa testo="Caricamento…" />
        : mie.length
          ? (
            <ul className="mediazioni-prossime">
              {mie.map((m) => <li key={m.id}><VoceMediazione m={m} compatta /></li>)}
            </ul>
          )
          : <Vuoto testo="Nessuna mediazione in programma." />}
    </Riquadro>
  )
}
